import { supabase } from '../../supabase.js';
import { validateAndGetTenantUuid } from '../../utils/tenantUtils.js';
import { ServiceError } from '../../utils/errorHandler.js';

class RevenueShareService {
  constructor() {
    this.revenueTransactionsTable = 'revenue_transactions';
    this.developerPayoutsTable = 'developer_payouts';
    this.appDevelopersTable = 'app_developers';
    this.appsTable = 'apps';
  }

  // Create revenue transaction
  async createRevenueTransaction(appId, developerId, tenantId, amount, transactionType = 'token_purchase') {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      // Get developer's revenue share percentage
      const { data: developer, error: devError } = await supabase
        .from(this.appDevelopersTable)
        .select('revenue_share_percentage')
        .eq('id', developerId)
        .single();
      
      if (devError) throw devError;
      
      const revenueSharePercentage = developer.revenue_share_percentage || 70.00; // Default 70%
      const platformFee = amount * (1 - revenueSharePercentage / 100);
      const developerShare = amount * (revenueSharePercentage / 100);
      
      const transactionData = {
        app_id: appId,
        developer_id: developerId,
        tenant_id: tenantUuid,
        transaction_type: transactionType,
        amount: amount,
        platform_fee: platformFee,
        developer_share: developerShare,
        transaction_date: new Date().toISOString(),
        status: 'pending'
      };
      
      const { data, error } = await supabase
        .from(this.revenueTransactionsTable)
        .insert(transactionData)
        .select()
        .single();
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('REVENUE_TRANSACTION_CREATION_FAILED', error.message);
    }
  }

  // Get developer's revenue transactions
  async getDeveloperRevenueTransactions(developerId, tenantId = null, limit = 50) {
    try {
      let query = supabase
        .from(this.revenueTransactionsTable)
        .select(`
          *,
          apps (
            app_name,
            app_category
          )
        `)
        .eq('developer_id', developerId)
        .order('transaction_date', { ascending: false })
        .limit(limit);
      
      if (tenantId) {
        const tenantUuid = await validateAndGetTenantUuid(tenantId);
        query = query.eq('tenant_id', tenantUuid);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('DEVELOPER_REVENUE_TRANSACTIONS_FETCH_FAILED', error.message);
    }
  }

  // Get developer's revenue statistics
  async getDeveloperRevenueStats(developerId, tenantId = null, dateRange = null) {
    try {
      let query = supabase
        .from(this.revenueTransactionsTable)
        .select('amount, platform_fee, developer_share, transaction_date, status')
        .eq('developer_id', developerId);
      
      if (tenantId) {
        const tenantUuid = await validateAndGetTenantUuid(tenantId);
        query = query.eq('tenant_id', tenantUuid);
      }
      
      if (dateRange) {
        query = query
          .gte('transaction_date', dateRange.start)
          .lte('transaction_date', dateRange.end);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      const stats = {
        total_revenue: data.reduce((sum, tx) => sum + (tx.amount || 0), 0),
        total_platform_fees: data.reduce((sum, tx) => sum + (tx.platform_fee || 0), 0),
        total_developer_share: data.reduce((sum, tx) => sum + (tx.developer_share || 0), 0),
        transaction_count: data.length,
        pending_transactions: data.filter(tx => tx.status === 'pending').length,
        processed_transactions: data.filter(tx => tx.status === 'processed').length,
        paid_transactions: data.filter(tx => tx.status === 'paid').length
      };
      
      return { success: true, data: stats };
    } catch (error) {
      throw new ServiceError('DEVELOPER_REVENUE_STATS_FETCH_FAILED', error.message);
    }
  }

  // Get revenue statistics by app
  async getAppRevenueStats(appId, tenantId = null, dateRange = null) {
    try {
      let query = supabase
        .from(this.revenueTransactionsTable)
        .select('amount, platform_fee, developer_share, transaction_date, status')
        .eq('app_id', appId);
      
      if (tenantId) {
        const tenantUuid = await validateAndGetTenantUuid(tenantId);
        query = query.eq('tenant_id', tenantUuid);
      }
      
      if (dateRange) {
        query = query
          .gte('transaction_date', dateRange.start)
          .lte('transaction_date', dateRange.end);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      const stats = {
        total_revenue: data.reduce((sum, tx) => sum + (tx.amount || 0), 0),
        total_platform_fees: data.reduce((sum, tx) => sum + (tx.platform_fee || 0), 0),
        total_developer_share: data.reduce((sum, tx) => sum + (tx.developer_share || 0), 0),
        transaction_count: data.length,
        average_transaction_value: data.length > 0 ? 
          data.reduce((sum, tx) => sum + (tx.amount || 0), 0) / data.length : 0
      };
      
      return { success: true, data: stats };
    } catch (error) {
      throw new ServiceError('APP_REVENUE_STATS_FETCH_FAILED', error.message);
    }
  }

  // Process revenue transactions (mark as processed)
  async processRevenueTransactions(transactionIds) {
    try {
      const { data, error } = await supabase
        .from(this.revenueTransactionsTable)
        .update({ 
          status: 'processed',
          updated_at: new Date().toISOString()
        })
        .in('id', transactionIds)
        .select();
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('REVENUE_TRANSACTIONS_PROCESSING_FAILED', error.message);
    }
  }

  // Create developer payout
  async createDeveloperPayout(developerId, payoutPeriodStart, payoutPeriodEnd, paymentMethod = 'bank_transfer') {
    try {
      // Get all processed transactions for the period
      const { data: transactions, error: txError } = await supabase
        .from(this.revenueTransactionsTable)
        .select('amount, platform_fee, developer_share')
        .eq('developer_id', developerId)
        .eq('status', 'processed')
        .gte('transaction_date', payoutPeriodStart)
        .lte('transaction_date', payoutPeriodEnd);
      
      if (txError) throw txError;
      
      if (transactions.length === 0) {
        throw new Error('No processed transactions found for the specified period');
      }
      
      const totalAmount = transactions.reduce((sum, tx) => sum + (tx.amount || 0), 0);
      const totalPlatformFees = transactions.reduce((sum, tx) => sum + (tx.platform_fee || 0), 0);
      const netAmount = transactions.reduce((sum, tx) => sum + (tx.developer_share || 0), 0);
      
      const payoutData = {
        developer_id: developerId,
        payout_period_start: payoutPeriodStart,
        payout_period_end: payoutPeriodEnd,
        total_amount: totalAmount,
        platform_fee: totalPlatformFees,
        net_amount: netAmount,
        payment_method: paymentMethod,
        payment_status: 'pending',
        transaction_id: `PAYOUT_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        created_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from(this.developerPayoutsTable)
        .insert(payoutData)
        .select()
        .single();
      
      if (error) throw error;
      
      // Mark transactions as paid
      await this.markTransactionsAsPaid(transactions.map(tx => tx.id));
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('DEVELOPER_PAYOUT_CREATION_FAILED', error.message);
    }
  }

  // Mark transactions as paid
  async markTransactionsAsPaid(transactionIds) {
    try {
      const { error } = await supabase
        .from(this.revenueTransactionsTable)
        .update({ 
          status: 'paid',
          updated_at: new Date().toISOString()
        })
        .in('id', transactionIds);
      
      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      throw new ServiceError('TRANSACTIONS_MARK_AS_PAID_FAILED', error.message);
    }
  }

  // Get developer's payout history
  async getDeveloperPayoutHistory(developerId, limit = 50) {
    try {
      const { data, error } = await supabase
        .from(this.developerPayoutsTable)
        .select('*')
        .eq('developer_id', developerId)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('DEVELOPER_PAYOUT_HISTORY_FETCH_FAILED', error.message);
    }
  }

  // Update payout status
  async updatePayoutStatus(payoutId, status, transactionId = null) {
    try {
      const updateData = {
        payment_status: status,
        updated_at: new Date().toISOString()
      };
      
      if (status === 'paid' && transactionId) {
        updateData.payout_date = new Date().toISOString();
        updateData.transaction_id = transactionId;
      }
      
      const { data, error } = await supabase
        .from(this.developerPayoutsTable)
        .update(updateData)
        .eq('id', payoutId)
        .select()
        .single();
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('PAYOUT_STATUS_UPDATE_FAILED', error.message);
    }
  }

  // Get platform revenue statistics
  async getPlatformRevenueStats(tenantId = null, dateRange = null) {
    try {
      let query = supabase
        .from(this.revenueTransactionsTable)
        .select('amount, platform_fee, developer_share, transaction_date, status');
      
      if (tenantId) {
        const tenantUuid = await validateAndGetTenantUuid(tenantId);
        query = query.eq('tenant_id', tenantUuid);
      }
      
      if (dateRange) {
        query = query
          .gte('transaction_date', dateRange.start)
          .lte('transaction_date', dateRange.end);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      const stats = {
        total_revenue: data.reduce((sum, tx) => sum + (tx.amount || 0), 0),
        total_platform_fees: data.reduce((sum, tx) => sum + (tx.platform_fee || 0), 0),
        total_developer_payouts: data.reduce((sum, tx) => sum + (tx.developer_share || 0), 0),
        transaction_count: data.length,
        average_transaction_value: data.length > 0 ? 
          data.reduce((sum, tx) => sum + (tx.amount || 0), 0) / data.length : 0,
        platform_fee_percentage: data.length > 0 ? 
          (data.reduce((sum, tx) => sum + (tx.platform_fee || 0), 0) / 
           data.reduce((sum, tx) => sum + (tx.amount || 0), 0)) * 100 : 0
      };
      
      return { success: true, data: stats };
    } catch (error) {
      throw new ServiceError('PLATFORM_REVENUE_STATS_FETCH_FAILED', error.message);
    }
  }

  // Get pending payouts
  async getPendingPayouts(limit = 50) {
    try {
      const { data, error } = await supabase
        .from(this.developerPayoutsTable)
        .select(`
          *,
          app_developers (
            developer_name,
            developer_email,
            company_name
          )
        `)
        .eq('payment_status', 'pending')
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('PENDING_PAYOUTS_FETCH_FAILED', error.message);
    }
  }
}

export const revenueShareService = new RevenueShareService();
